import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Chip } from '@mui/material';
import React, { useState } from 'react';
import { DEFAULT_PLANOGRAM_COMPLIANCE_CONFIG } from '../../../constants/planogramComplianceConfig';
import { ComplianceStatus, PlanogramComplianceConfig } from '../../../types';

interface PlanogramComplianceAdvancedSettingsProps {
  config: PlanogramComplianceConfig;
  onConfigChange: (updates: Partial<PlanogramComplianceConfig>) => void;
  onReset: () => void;
  children?: React.ReactNode;
  isV2: boolean;
}

interface BooleanToggleProps {
  label: string;
  configKey: keyof PlanogramComplianceConfig;
  checked: boolean | null | undefined;
  onChange: (key: keyof PlanogramComplianceConfig, value: boolean) => void;
}

const BooleanToggle: React.FC<BooleanToggleProps> = ({ label, configKey, checked, onChange }) => (
  <div className="flex items-center gap-2">
    <span className="text-sm font-medium text-gray-700">{label}:</span>
    <button
      onClick={() => onChange(configKey, !checked)}
      className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
        checked
          ? 'bg-blue-600 border-blue-600 text-white'
          : 'bg-white border-gray-300 hover:border-gray-400'
      }`}
      title={`Toggle ${label}`}
    >
      {checked && (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </button>
  </div>
);

export const PlanogramComplianceAdvancedSettings: React.FC<
  PlanogramComplianceAdvancedSettingsProps
> = ({ config, onConfigChange, onReset, children, isV2 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleNumberChange = (key: keyof PlanogramComplianceConfig, value: string) => {
    const numValue = value === '' ? null : parseFloat(value);
    onConfigChange({ [key]: numValue });
  };

  const handleBooleanChange = (key: keyof PlanogramComplianceConfig, value: boolean) => {
    onConfigChange({ [key]: value });
  };

  const handleComplianceStatusToggle = (
    key: 'compliantStatus' | 'osaStatus' | 'compliantStatusToKeep',
    status: ComplianceStatus
  ) => {
    const currentArray = config[key] || [];
    const newArray = currentArray.includes(status)
      ? currentArray.filter((s) => s !== status)
      : [...currentArray, status];

    onConfigChange({ [key]: newArray.length > 0 ? newArray : null });
  };

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Advanced Settings</span>
          {/* Show indicator if any settings differ from defaults */}
          {JSON.stringify(config) !== JSON.stringify(DEFAULT_PLANOGRAM_COMPLIANCE_CONFIG) && (
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Modified</span>
          )}
        </div>
        {isExpanded ? (
          <ExpandLessIcon className="text-gray-600" />
        ) : (
          <ExpandMoreIcon className="text-gray-600" />
        )}
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="p-4 space-y-6 bg-white">
          {/* Optional children (e.g., PlanogramComplianceToggles) */}
          {children}
          <div className={`space-y-6 ${isV2 ? 'hidden' : ''}`}>
            {/* Number Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Similarity Threshold
                </label>
                <input
                  type="number"
                  value={config.minSimilarityThreshold ?? ''}
                  onChange={(e) => handleNumberChange('minSimilarityThreshold', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.87"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Stacking Similarity Threshold
                </label>
                <input
                  type="number"
                  value={config.minStackingSimilarityThreshold ?? ''}
                  onChange={(e) =>
                    handleNumberChange('minStackingSimilarityThreshold', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="1"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Difference Threshold
                </label>
                <input
                  type="number"
                  value={config.maxDifferenceThreshold ?? ''}
                  onChange={(e) => handleNumberChange('maxDifferenceThreshold', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.04"
                  step="0.01"
                />
              </div>

              {/* Min SSM Confidence */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min SSM Confidence
                </label>
                <input
                  type="number"
                  value={config.minSsmConfidence ?? ''}
                  onChange={(e) => handleNumberChange('minSsmConfidence', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.80"
                  step="0.01"
                />
              </div>

              {/* Min Intersection Area */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Intersection Area
                </label>
                <input
                  type="number"
                  value={config.minIntersectionArea ?? ''}
                  onChange={(e) => handleNumberChange('minIntersectionArea', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.2"
                  step="0.01"
                />
              </div>

              {/* OOS Override Area */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  OOS Override Area
                </label>
                <input
                  type="number"
                  value={config.oosOverrideArea ?? ''}
                  onChange={(e) => handleNumberChange('oosOverrideArea', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                  step="0.01"
                />
              </div>

              {/* OOS Override Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  OOS Override Threshold
                </label>
                <input
                  type="number"
                  value={config.oosOverrideThreshold ?? ''}
                  onChange={(e) => handleNumberChange('oosOverrideThreshold', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                  step="0.01"
                />
              </div>

              {/* Bottom Threshold Cartons From Polygons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bottom Threshold Cartons From Polygons
                </label>
                <input
                  type="number"
                  value={config.bottomThresholdCartonsFromPolygons ?? ''}
                  onChange={(e) =>
                    handleNumberChange('bottomThresholdCartonsFromPolygons', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                  step="0.01"
                />
              </div>

              {/* Obstruction Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Obstruction Threshold
                </label>
                <input
                  type="number"
                  value={config.obstructionThreshold ?? ''}
                  onChange={(e) => handleNumberChange('obstructionThreshold', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                  step="0.01"
                />
              </div>

              {/* Price Tag Glare Override % */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price Tag Glare Override %
                </label>
                <input
                  type="number"
                  value={config.priceTagGlareOverridePct ?? ''}
                  onChange={(e) => handleNumberChange('priceTagGlareOverridePct', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.0"
                  step="0.01"
                />
              </div>

              {/* Group Move Bridge Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Move Bridge Size
                </label>
                <input
                  type="number"
                  value={config.groupMoveBridgeSize ?? ''}
                  onChange={(e) => handleNumberChange('groupMoveBridgeSize', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="1"
                  step="1"
                />
              </div>

              {/* Group Move Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Move Size
                </label>
                <input
                  type="number"
                  value={config.groupMoveSize ?? ''}
                  onChange={(e) => handleNumberChange('groupMoveSize', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="2"
                  step="1"
                />
              </div>

              {/* Faceover Reset Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Faceover Reset Threshold
                </label>
                <input
                  type="number"
                  value={config.faceoverResetThreshold ?? ''}
                  onChange={(e) => handleNumberChange('faceoverResetThreshold', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.5"
                  step="0.01"
                />
              </div>

              {/* Forgiveness Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Forgiveness Threshold
                </label>
                <input
                  type="number"
                  value={config.forgivenessThreshold ?? ''}
                  onChange={(e) => handleNumberChange('forgivenessThreshold', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="1"
                  step="1"
                />
              </div>
            </div>

            {/* Boolean Toggles */}
            <div className="flex flex-wrap items-center gap-4">
              <BooleanToggle
                label="Use PT Template Boxes"
                configKey="usePtTemplateBoxes"
                checked={config.usePtTemplateBoxes}
                onChange={handleBooleanChange}
              />
              <BooleanToggle
                label="Use Bleedovers"
                configKey="useBleedovers"
                checked={config.useBleedovers}
                onChange={handleBooleanChange}
              />
              <BooleanToggle
                label="Use Small Bleedovers"
                configKey="useSmallBleedovers"
                checked={config.useSmallBleedovers}
                onChange={handleBooleanChange}
              />
              <BooleanToggle
                label="Bleedover Ignore Mod Order"
                configKey="bleedoverIgnoreModOrder"
                checked={config.bleedoverIgnoreModOrder}
                onChange={handleBooleanChange}
              />
              <BooleanToggle
                label="Bleedover Mirror Position"
                configKey="bleedoverMirrorPosition"
                checked={config.bleedoverMirrorPosition}
                onChange={handleBooleanChange}
              />
              <BooleanToggle
                label="Correct Group Moves"
                configKey="correctGroupMoves"
                checked={config.correctGroupMoves}
                onChange={handleBooleanChange}
              />
              <BooleanToggle
                label="Create Tasks"
                configKey="createTasks"
                checked={config.createTasks}
                onChange={handleBooleanChange}
              />
              <BooleanToggle
                label="Price Tag Glare Enabled"
                configKey="priceTagGlareEnabled"
                checked={config.priceTagGlareEnabled}
                onChange={handleBooleanChange}
              />
              <BooleanToggle
                label="Remove Outside Camera Actions"
                configKey="removeOutsideCameraActions"
                checked={config.removeOutsideCameraActions}
                onChange={handleBooleanChange}
              />
              <BooleanToggle
                label="Use Item Number"
                configKey="useItemNumber"
                checked={config.useItemNumber}
                onChange={handleBooleanChange}
              />
              <BooleanToggle
                label="Apply Forgiveness Threshold"
                configKey="applyForgivenessThreshold"
                checked={config.applyForgivenessThreshold}
                onChange={handleBooleanChange}
              />
            </div>

            {/* ComplianceStatus Arrays */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Compliant Status
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.values(ComplianceStatus).map((status) => (
                    <Chip
                      key={status}
                      label={status.replace(/_/g, ' ')}
                      onClick={() => handleComplianceStatusToggle('compliantStatus', status)}
                      color={
                        (config.compliantStatus || []).includes(status) ? 'primary' : 'default'
                      }
                      variant={
                        (config.compliantStatus || []).includes(status) ? 'filled' : 'outlined'
                      }
                      sx={{ textTransform: 'capitalize' }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">OSA Status</label>
                <div className="flex flex-wrap gap-2">
                  {Object.values(ComplianceStatus).map((status) => (
                    <Chip
                      key={status}
                      label={status.replace(/_/g, ' ')}
                      onClick={() => handleComplianceStatusToggle('osaStatus', status)}
                      color={(config.osaStatus || []).includes(status) ? 'primary' : 'default'}
                      variant={(config.osaStatus || []).includes(status) ? 'filled' : 'outlined'}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Compliant Status To Keep (Optional)
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.values(ComplianceStatus).map((status) => (
                    <Chip
                      key={status}
                      label={status.replace(/_/g, ' ')}
                      onClick={() => handleComplianceStatusToggle('compliantStatusToKeep', status)}
                      color={
                        (config.compliantStatusToKeep || []).includes(status)
                          ? 'primary'
                          : 'default'
                      }
                      variant={
                        (config.compliantStatusToKeep || []).includes(status)
                          ? 'filled'
                          : 'outlined'
                      }
                      sx={{ textTransform: 'capitalize' }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={onReset}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
              >
                <RestartAltIcon fontSize="small" />
                Reset to Defaults
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
